// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import * as Sentry from '@sentry/react';
import { handleAPIError } from '@/api/error-utils';
import { WellFormedError } from '@/api/types';
import { AddInvoiceInput, addInvoice } from '@/billing/api/add-invoice';
import { invalidateInvoiceQueries } from '@/billing/hooks/invalidate-invoice-queries';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

// 404 here means the (customer, project) pair no longer matches server state —
// typically a stale picker cache. The dialog refetches + tells the user;
// not a Sentry-worthy failure.
const EXPECTED_HTTP_STATUS_CODES = new Set<number>([404]);

interface UseAddInvoiceOptions {
  onSuccess?: (invoiceKey: string) => void;
  onError?: (apiError: WellFormedError) => void;
}

export function useAddInvoice(options: UseAddInvoiceOptions = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: AddInvoiceInput) => addInvoice(input),
    onSuccess: async (invoice) => {
      await Promise.all([
        invalidateInvoiceQueries({
          queryClient,
          invoiceKey: invoice.key,
          includeCharges: false,
        }),
        queryClient.invalidateQueries({ queryKey: ['sparse-projects'] }),
      ]);
      options.onSuccess?.(invoice.key);
    },
    onError: (error: AxiosError) => {
      const apiError = handleAPIError(error);
      const isExpected = EXPECTED_HTTP_STATUS_CODES.has(
        apiError.httpStatusCode
      );
      if (!apiError.sentToSentry && !isExpected) {
        Sentry.captureException(error);
      }
      options.onError?.(apiError);
    },
  });
}
